import type { DatahoseFormState } from "@/widget-components/equities-widgets/shared/searchFormConfig";

export type LayoutItem = {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
};

export type BreakpointLayouts = {
  lg: LayoutItem[];
  md: LayoutItem[];
  sm: LayoutItem[];
  xs: LayoutItem[];
  xxs: LayoutItem[];
};

/** Persisted search params for a widget's search panel (mirrors filter/highlight
 *  persistence). Re-applied on mount so a search stays "in play" across Done,
 *  tab switches, and reloads. */
export type WidgetSearchData = {
  query?: string;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  exactMatch?: boolean;
};

/** Persisted table state for the datahose/equities feed widgets: column-header
 *  filters, the mag-glass search form's raw fields, and column layout. Without
 *  this the state lives in component `useState` and dies with the Headless UI
 *  TabPanel unmount on every tab switch. */
export type WidgetTableState = {
  /** TanStack `ColumnFiltersState` — `value` is a string, a NumericFilterValue,
   *  or a DateRangeFilterValue, all plain JSON. */
  columnFilters?: { id: string; value: unknown }[];
  columnSizing?: Record<string, number>;
  columnOrder?: string[];
  /** Raw search-form fields; request params are re-derived via buildSearchParams. */
  searchForm?: DatahoseFormState;
};

export type WidgetFilterData = {
  filter?: Record<string, string[]>;
  highlight?: Record<string, string[]>;
  /**
   * The order of this widget's highlighted lists — the first a row matches
   * paints it. Keys are `watchlist:<id>` / `combined:<id>`; see
   * src/shared/utils/highlightOrder.ts. Absent until the widget is seeded or
   * its highlights change.
   */
  highlightOrder?: string[];
  search?: WidgetSearchData;
  table?: WidgetTableState;
};

export type DashboardTab = {
  id: string;
  title: string;
  layout: BreakpointLayouts;
  enabledWidgets: string[];
  widgetFilters?: Record<string, WidgetFilterData>;
};

// Breakpoint column counts for first-fit placement
export const breakpointCols: Record<string, number> = {
  lg: 16,
  md: 10,
  sm: 6,
  xs: 2,
  xxs: 1,
};

/**
 * Check if a widget can fit at a given position in the occupancy grid
 */
function canFit(
  grid: boolean[][],
  startX: number,
  startY: number,
  width: number,
  height: number
): boolean {
  for (let y = startY; y < startY + height; y++) {
    for (let x = startX; x < startX + width; x++) {
      if (!grid[y] || grid[y][x]) return false;
    }
  }
  return true;
}

/**
 * Find the first position where a widget can fit in the grid
 * Scans left-to-right, top-to-bottom to find gaps
 */
export function findFirstFitPosition(
  layout: LayoutItem[],
  widgetSize: { w: number; h: number },
  cols: number
): { x: number; y: number } {
  // Handle empty layout
  if (layout.length === 0) {
    return { x: 0, y: 0 };
  }

  // Build occupancy grid from existing layout
  const maxY = Math.max(...layout.map((item) => item.y + item.h), 0);
  const gridHeight = maxY + widgetSize.h + 1;
  const grid: boolean[][] = Array(gridHeight)
    .fill(null)
    .map(() => Array(cols).fill(false));

  // Mark occupied cells
  for (const item of layout) {
    for (let y = item.y; y < item.y + item.h; y++) {
      for (let x = item.x; x < item.x + item.w; x++) {
        if (grid[y]) grid[y][x] = true;
      }
    }
  }

  // Scan for first fit (left-to-right, top-to-bottom)
  for (let y = 0; y <= maxY + 1; y++) {
    for (let x = 0; x <= cols - widgetSize.w; x++) {
      if (canFit(grid, x, y, widgetSize.w, widgetSize.h)) {
        return { x, y };
      }
    }
  }

  // Fallback: place below everything
  return { x: 0, y: maxY };
}

export const HomeTab = {
  widgets: ["CreateHeadline-1", "NewsquawkHeadlines-1", "FirehoseAll-1"],
  layouts: {
    lg: [
      /** No. of columns for lg = 16 */
      { i: "NewsquawkHeadlines-1", x: 0, y: 0, w: 5, h: 10 },
      { i: "FirehoseAll-1", x: 5, y: 0, w: 5, h: 10 },
      { i: "CreateHeadline-1", x: 10, y: 0, w: 6, h: 10 },
    ],
    md: [
      /** No. of columns for md = 10 */
      { i: "NewsquawkHeadlines-1", x: 0, y: 0, w: 4, h: 10 },
      { i: "FirehoseAll-1", x: 4, y: 0, w: 3, h: 10 },
      { i: "CreateHeadline-1", x: 7, y: 0, w: 3, h: 10 },
    ],
    sm: [
      /** No. of columns for sm = 6 */
      { i: "NewsquawkHeadlines-1", x: 0, y: 0, w: 2, h: 8 },
      { i: "FirehoseAll-1", x: 2, y: 0, w: 2, h: 8 },
      { i: "CreateHeadline-1", x: 4, y: 0, w: 2, h: 8 },
    ],
    xs: [
      /** No. of columns for xs = 2 */
      { i: "NewsquawkHeadlines-1", x: 0, y: 0, w: 2, h: 4 },
      { i: "FirehoseAll-1", x: 0, y: 4, w: 1, h: 4 },
      { i: "CreateHeadline-1", x: 0, y: 4, w: 2, h: 8 },
    ],
    xxs: [
      /** No. of columns for xxs = 1 */
      { i: "CreateHeadline-1", x: 0, y: 0, w: 1, h: 4 },
      { i: "NewsquawkHeadlines-1", x: 0, y: 4, w: 1, h: 4 },
      { i: "FirehoseAll-1", x: 0, y: 8, w: 1, h: 4 },
    ],
  },
};

export const ClientHomeTab = {
  widgets: [
    "NewsquawkHeadlines-1",
    "TruthSocial-1",
    "FinX-1",
    "EconomicCalendar-1",
  ],
  layouts: {
    lg: [
      /** No. of columns for lg = 16, max h = 10 */
      { i: "NewsquawkHeadlines-1", x: 0, y: 0, w: 6, h: 10 },
      { i: "TruthSocial-1", x: 6, y: 0, w: 5, h: 5 },
      { i: "FinX-1", x: 6, y: 5, w: 5, h: 5 },
      { i: "EconomicCalendar-1", x: 11, y: 0, w: 5, h: 10 },
    ],
    md: [
      /** No. of columns for md = 10, max h = 10 */
      { i: "NewsquawkHeadlines-1", x: 0, y: 0, w: 5, h: 10 },
      { i: "TruthSocial-1", x: 5, y: 0, w: 5, h: 3 },
      { i: "FinX-1", x: 5, y: 3, w: 5, h: 3 },
      { i: "EconomicCalendar-1", x: 5, y: 6, w: 5, h: 4 },
    ],
    sm: [
      /** No. of columns for sm = 6, max h = 10 */
      { i: "NewsquawkHeadlines-1", x: 0, y: 0, w: 3, h: 10 },
      { i: "TruthSocial-1", x: 3, y: 0, w: 3, h: 3 },
      { i: "FinX-1", x: 3, y: 3, w: 3, h: 3 },
      { i: "EconomicCalendar-1", x: 3, y: 6, w: 3, h: 4 },
    ],
    xs: [
      /** No. of columns for xs = 2, stack vertically */
      { i: "NewsquawkHeadlines-1", x: 0, y: 0, w: 2, h: 5 },
      { i: "TruthSocial-1", x: 0, y: 5, w: 2, h: 4 },
      { i: "FinX-1", x: 0, y: 9, w: 2, h: 4 },
      { i: "EconomicCalendar-1", x: 0, y: 13, w: 2, h: 4 },
    ],
    xxs: [
      /** No. of columns for xxs = 1, stack vertically */
      { i: "NewsquawkHeadlines-1", x: 0, y: 0, w: 1, h: 5 },
      { i: "TruthSocial-1", x: 0, y: 5, w: 1, h: 4 },
      { i: "FinX-1", x: 0, y: 9, w: 1, h: 4 },
      { i: "EconomicCalendar-1", x: 0, y: 13, w: 1, h: 4 },
    ],
  },
};

// Old busy view
// export const ClientHomeTab = {
//   widgets: [
//     "NewsquawkHeadlines-1",
//     "TruthSocial-1",
//     "StocksNews-1",
//     "FinX-1",
//     "InvEconCalendar-1",
//     "TvAdvancedRealTimeChart-1",
//     // "CreateHeadline-1",
//   ],
//   layouts: {
//     lg: [
//       { i: "NewsquawkHeadlines-1", x: 0, y: 0, w: 4, h: 10 },
//       { i: "TruthSocial-1", x: 8, y: 0, w: 3, h: 4 },
//       { i: "StocksNews-1", x: 4, y: 5, w: 4, h: 5 },
//       { i: "FinX-1", x: 4, y: 0, w: 4, h: 5 },
//       { i: "InvEconCalendar-1", x: 11, y: 0, w: 5, h: 4 },
//       { i: "TvAdvancedRealTimeChart-1", x: 8, y: 4, w: 8, h: 6 },
//     ],

//     md: [
//       { i: "NewsquawkHeadlines-1", x: 0, y: 0, w: 5, h: 4 },
//       { i: "FinX-1", x: 0, y: 0, w: 4, h: 5 },
//       { i: "TruthSocial-1", x: 0, y: 0, w: 4, h: 5 },
//       { i: "CreateHeadline-1", x: 0, y: 0, w: 4, h: 4 },
//       { i: "StocksNews-1", x: 0, y: 0, w: 4, h: 5 },
//       { i: "TvAdvancedRealTimeChart-1", x: 0, y: 4, w: 9, h: 5 },
//       { i: "InvEconCalendar-1", x: 0, y: 0, w: 4, h: 4 },
//     ],

//     sm: [
//       { i: "NewsquawkHeadlines-1", x: 0, y: 0, w: 5, h: 4 },
//       { i: "FinX-1", x: 0, y: 0, w: 4, h: 5 },
//       { i: "TruthSocial-1", x: 0, y: 0, w: 4, h: 5 },
//       { i: "CreateHeadline-1", x: 0, y: 0, w: 4, h: 4 },
//       { i: "StocksNews-1", x: 0, y: 0, w: 4, h: 5 },
//       { i: "TvAdvancedRealTimeChart-1", x: 0, y: 4, w: 9, h: 5 },
//       { i: "InvEconCalendar-1", x: 0, y: 0, w: 4, h: 4 },
//     ],

//     xs: [
//       { i: "NewsquawkHeadlines-1", x: 0, y: 0, w: 5, h: 4 },
//       { i: "FinX-1", x: 0, y: 0, w: 4, h: 5 },
//       { i: "TruthSocial-1", x: 0, y: 0, w: 4, h: 5 },
//       { i: "CreateHeadline-1", x: 0, y: 0, w: 4, h: 4 },
//       { i: "StocksNews-1", x: 0, y: 0, w: 4, h: 5 },
//       { i: "TvAdvancedRealTimeChart-1", x: 0, y: 4, w: 9, h: 5 },
//       { i: "InvEconCalendar-1", x: 0, y: 0, w: 4, h: 4 },
//     ],

//     xxs: [
//       { i: "NewsquawkHeadlines-1", x: 0, y: 0, w: 1, h: 4 },
//       { i: "TruthSocial-1", x: 0, y: 9, w: 1, h: 5 },
//       { i: "FinX-1", x: 0, y: 19, w: 1, h: 5 },
//       { i: "StocksNews-1", x: 0, y: 0, w: 4, h: 5 },
//       { i: "TvAdvancedRealTimeChart-1", x: 0, y: 4, w: 9, h: 5 },
//       { i: "InvEconCalendar-1", x: 0, y: 0, w: 4, h: 4 },
//     ],
//   },
// };

export const V2DemoTab = {
  widgets: [
    "NewsquawkHeadlines-1",
    "TruthSocial-1",
    "StocksNews-1",
    "FinX-1",
    "CryptoWeb-1",
    "BloombergLive-1",
    "InvEconCalendar-1",
  ],
  layouts: {
    lg: [
      { i: "NewsquawkHeadlines-1", x: 0, y: 0, w: 4, h: 10 },
      { i: "TruthSocial-1", x: 4, y: 0, w: 4, h: 3 },
      { i: "StocksNews-1", x: 4, y: 2, w: 4, h: 7 },
      { i: "FinX-1", x: 8, y: 0, w: 4, h: 5 },
      { i: "InvEconCalendar-1", x: 8, y: 5, w: 4, h: 5 },
      { i: "BloombergLive-1", x: 12, y: 0, w: 4, h: 3 },
      { i: "CryptoWeb-1", x: 12, y: 3, w: 4, h: 7 },
    ],
    md: [
      { i: "NewsquawkHeadlines-1", x: 0, y: 0, w: 4, h: 10 },
      { i: "TruthSocial-1", x: 4, y: 0, w: 3, h: 3 },
      { i: "StocksNews-1", x: 4, y: 3, w: 3, h: 7 },

      { i: "BloombergLive-1", x: 8, y: 10, w: 3, h: 3 },
      { i: "CryptoWeb-1", x: 8, y: 13, w: 3, h: 7 },

      { i: "FinX-1", x: 0, y: 10, w: 5, h: 5 },
      { i: "InvEconCalendar-1", x: 5, y: 10, w: 5, h: 5 },
    ],
    sm: [
      { i: "NewsquawkHeadlines-1", x: 0, y: 0, w: 3, h: 6 },
      { i: "TruthSocial-1", x: 3, y: 0, w: 3, h: 3 },
      { i: "StocksNews-1", x: 4, y: 3, w: 3, h: 3 },

      { i: "BloombergLive-1", x: 8, y: 10, w: 3, h: 3 },
      { i: "CryptoWeb-1", x: 8, y: 13, w: 3, h: 7 },

      { i: "FinX-1", x: 0, y: 10, w: 3, h: 5 },
      { i: "InvEconCalendar-1", x: 0, y: 10, w: 3, h: 5 },
    ],
  },
};

export const BrokerViewTab = {
  widgets: [
    "FinX-1",
    "NewsquawkHeadlines-1",
    "StocksNews-1",
    "TruthSocial-1",
    "TvAdvancedRealTimeChart-1",
    "BrokerPriceLadderYM-1",
    "BrokerPriceLadderSP-1",
  ],
  layouts: {
    lg: [
      { i: "FinX-1", x: 12, y: 6, w: 4, h: 3 },
      { i: "NewsquawkHeadlines-1", x: 0, y: 0, w: 4, h: 9 },
      { i: "StocksNews-1", x: 8, y: 6, w: 4, h: 3 },
      { i: "TruthSocial-1", x: 4, y: 6, w: 4, h: 3 },
      { i: "TvAdvancedRealTimeChart-1", x: 10, y: 0, w: 6, h: 6 },
      { i: "BrokerPriceLadderYM-1", x: 4, y: 0, w: 3, h: 6 },
      { i: "BrokerPriceLadderSP-1", x: 7, y: 0, w: 3, h: 6 },
    ],
    md: [
      { i: "NewsquawkHeadlines-1", x: 0, y: 0, w: 4, h: 10 },
      { i: "BrokerPriceLadderYM-1", x: 0, y: 0, w: 3, h: 7 },
      { i: "TvAdvancedRealTimeChart-1", x: 0, y: 4, w: 9, h: 5 },
      { i: "TruthSocial-1", x: 0, y: 0, w: 4, h: 5 },
      { i: "BrokerPriceLadderSP-1", x: 0, y: 0, w: 3, h: 7 },
      { i: "StocksNews-1", x: 0, y: 0, w: 4, h: 5 },
      { i: "FinX-1", x: 0, y: 0, w: 4, h: 5 },
    ],
    sm: [
      { i: "NewsquawkHeadlines-1", x: 0, y: 0, w: 2, h: 8 },
      { i: "BrokerPriceLadderYM-1", x: 0, y: 0, w: 3, h: 7 },
      { i: "TvAdvancedRealTimeChart-1", x: 0, y: 4, w: 9, h: 5 },
      { i: "TruthSocial-1", x: 0, y: 0, w: 4, h: 5 },
      { i: "BrokerPriceLadderSP-1", x: 0, y: 0, w: 3, h: 7 },
      { i: "StocksNews-1", x: 0, y: 0, w: 4, h: 5 },
      { i: "FinX-1", x: 0, y: 0, w: 4, h: 5 },
    ],
    xs: [
      { i: "NewsquawkHeadlines-1", x: 0, y: 0, w: 2, h: 4 },
      { i: "BrokerPriceLadderYM-1", x: 0, y: 0, w: 3, h: 7 },
      { i: "TvAdvancedRealTimeChart-1", x: 0, y: 4, w: 9, h: 5 },
      { i: "TruthSocial-1", x: 0, y: 0, w: 4, h: 5 },
      { i: "BrokerPriceLadderSP-1", x: 0, y: 0, w: 3, h: 7 },
      { i: "StocksNews-1", x: 0, y: 0, w: 4, h: 5 },
      { i: "FinX-1", x: 0, y: 0, w: 4, h: 5 },
    ],
    xxs: [
      { i: "FinX-1", x: 0, y: 0, w: 1, h: 5 },
      { i: "NewsquawkHeadlines-1", x: 0, y: 5, w: 1, h: 4 },
      { i: "StocksNews-1", x: 0, y: 9, w: 1, h: 5 },
      { i: "TruthSocial-1", x: 0, y: 14, w: 1, h: 5 },
      { i: "TvAdvancedRealTimeChart-1", x: 0, y: 19, w: 1, h: 5 },
      { i: "BrokerPriceLadderYM-1", x: 0, y: 24, w: 1, h: 7 },
      { i: "BrokerPriceLadderSP-1", x: 0, y: 31, w: 1, h: 7 },
    ],
  },
};

/**
 * What a logged-out visitor sees before they've told us what they trade.
 *
 * Deliberately three of our own feeds and nothing else. No External Firehose
 * — a raw multi-source wire is the most confusing thing we own to meet first,
 * and it's on the Explore Feeds tab for anyone who wants it. No TradingView
 * chart either: a free third-party embed demonstrates nothing about Newsquawk
 * and was the widest thing on the canvas.
 *
 * The layout is packed at read time rather than written out here, because a
 * hand-authored h:10 grid leaves a dead band at the bottom of any tall screen
 * — which is exactly where the guide rail then floats, pointing at a widget
 * nowhere near it. packLayouts reads the real viewport instead.
 *
 * Most visitors only see this for a moment: picking an asset class in the
 * guide replaces it with a template built for that asset class. This is the
 * fallback for
 * anyone who chooses to look around on their own.
 */
export const LoggedOutTab = {
  widgets: [
    "NewsquawkHeadlines-1", // the Newsquawk headline feed, 15min delayed
    "TruthSocial-1", // 24h demo
    "FinX-1", // 24h demo
  ],
  /** Filled in at read time by getLoggedOutTabs(). */
  layouts: undefined as unknown as BreakpointLayouts,
};

export const LoggedOutExploreFeedsTab = {
  widgets: [
    "FirehoseAll-1", // External Firehose
    "CryptoWeb-1", // Crypto Blogs & Websites
    "EnergyX-1", // Energy X (Twitter)
    "TruthSocial-1", // Truth Social
    "StocksNews-1", // Equities News
    "FinX-1", // Fin X (Twitter)
  ],
  layouts: {
    lg: [
      /** No. of columns for lg = 16, 3x2 grid */
      { i: "FirehoseAll-1", x: 0, y: 0, w: 5, h: 10 },
      { i: "CryptoWeb-1", x: 5, y: 0, w: 5, h: 10 },
      { i: "EnergyX-1", x: 10, y: 0, w: 6, h: 5 },
      { i: "TruthSocial-1", x: 10, y: 5, w: 6, h: 5 },
      { i: "StocksNews-1", x: 0, y: 10, w: 8, h: 5 },
      { i: "FinX-1", x: 8, y: 10, w: 8, h: 5 },
    ],
    md: [
      /** No. of columns for md = 10 */
      { i: "FirehoseAll-1", x: 0, y: 0, w: 5, h: 8 },
      { i: "CryptoWeb-1", x: 5, y: 0, w: 5, h: 8 },
      { i: "EnergyX-1", x: 0, y: 8, w: 5, h: 5 },
      { i: "TruthSocial-1", x: 5, y: 8, w: 5, h: 5 },
      { i: "StocksNews-1", x: 0, y: 13, w: 5, h: 5 },
      { i: "FinX-1", x: 5, y: 13, w: 5, h: 5 },
    ],
    sm: [
      /** No. of columns for sm = 6 */
      { i: "FirehoseAll-1", x: 0, y: 0, w: 3, h: 6 },
      { i: "CryptoWeb-1", x: 3, y: 0, w: 3, h: 6 },
      { i: "EnergyX-1", x: 0, y: 6, w: 3, h: 5 },
      { i: "TruthSocial-1", x: 3, y: 6, w: 3, h: 5 },
      { i: "StocksNews-1", x: 0, y: 11, w: 3, h: 5 },
      { i: "FinX-1", x: 3, y: 11, w: 3, h: 5 },
    ],
    xs: [
      /** No. of columns for xs = 2, stack vertically */
      { i: "FirehoseAll-1", x: 0, y: 0, w: 2, h: 5 },
      { i: "CryptoWeb-1", x: 0, y: 5, w: 2, h: 5 },
      { i: "EnergyX-1", x: 0, y: 10, w: 2, h: 5 },
      { i: "TruthSocial-1", x: 0, y: 15, w: 2, h: 5 },
      { i: "StocksNews-1", x: 0, y: 20, w: 2, h: 5 },
      { i: "FinX-1", x: 0, y: 25, w: 2, h: 5 },
    ],
    xxs: [
      /** No. of columns for xxs = 1, stack vertically */
      { i: "FirehoseAll-1", x: 0, y: 0, w: 1, h: 5 },
      { i: "CryptoWeb-1", x: 0, y: 5, w: 1, h: 5 },
      { i: "EnergyX-1", x: 0, y: 10, w: 1, h: 5 },
      { i: "TruthSocial-1", x: 0, y: 15, w: 1, h: 5 },
      { i: "StocksNews-1", x: 0, y: 20, w: 1, h: 5 },
      { i: "FinX-1", x: 0, y: 25, w: 1, h: 5 },
    ],
  },
};

